/**
 * YUMIX — Meal Analysis Pipeline (single source of truth).
 *
 * Replaces the previous spaghetti where Dashboard.js had its own copy of
 * the photo flow AND useMealActions.js had a divergent copy. ALL meal
 * entry methods (photo, text, manual) now route through this module.
 *
 * Design principles (Yuri's "Reset" directive — Avr. 2026):
 *   • Ultra-light. Zero animations during transfers.
 *   • Compression FORCÉE < 500 KB before any network call.
 *   • Fasting/Ramadan checks deferred — never abort the analysis pre-flight.
 *   • Same robustness for photo, text, and manual paths.
 *   • Breadcrumbs at every hop for post-mortem visibility.
 *   • Standardized "Analyse difficile" toast on timeout.
 */
import { api } from '../context/AppContext';
import { compressImage } from './imageCompression';
import validateAnalysisShape from './validateAnalysisShape';
import ErrorLogger from './errorLogger';
import ScanTelemetry from './scanTelemetry';

// ============================================================================
// PRE-WARM (Iter. 404)
// ============================================================================
// Vision LLMs (Gemini Flash, GPT-4o-mini) have a 15-25 s cold-start when the
// user's account hasn't queried in a few minutes. We hide that latency by
// firing a tiny warmup ping the moment the user opens the meal upload modal
// — before they've even picked a photo.
//
// Client-side throttle: max once per 30 s per browser session. Backend also
// throttles to 1 warmup / 45 s per worker so concurrent users coalesce.
let _lastPrewarmAt = 0;
const PREWARM_MIN_INTERVAL_MS = 30 * 1000;

export function prewarmVision() {
  const now = Date.now();
  if (now - _lastPrewarmAt < PREWARM_MIN_INTERVAL_MS) return;
  _lastPrewarmAt = now;
  // Fire-and-forget — never block the UI on this.
  // Iter. 404.d — Removed the `meal:prewarm:dispatched` breadcrumb that
  // was generating phantom Post_Mortem_Crash reports in production.
  // Pre-warm is a perf hint, not a user-flow checkpoint — logging it
  // as a breadcrumb caused every page-reload-after-FAB-open to be
  // dispatched as a CRITICAL crash, polluting the Bug Radar.
  try {
    api.post('/meals/prewarm', {}, { timeout: 5000 }).catch(() => {});
  } catch {
    // absorb — perf hint, not a contract
  }
}

// ============================================================================
// STREAMING ANALYZE (Iter. 404 · Lot #13)
// ============================================================================
// Consumes the NDJSON event stream from POST /meals/analyze-stream and
// forwards each phase event to a caller-supplied `onPhase` callback. Resolves
// with the final `complete` payload (same shape as /meals/analyze) so the
// rest of the app code path is identical.
//
// On `block` events (ramadan / cultural), this function REJECTS with a
// special error.kind so the caller can re-submit via the legacy endpoint
// (which handles the override UX). On `error` events, rejects with an
// AxiosError-shaped object so existing mapAnalysisError() works.
//
// On network failure mid-stream, falls back to the legacy endpoint via
// idempotency replay (handled by the caller, not here).

/**
 * Send a meal photo for streaming analysis.
 *
 * @param {object} args
 * @param {string} args.base64           Image bytes, no data: prefix
 * @param {string} [args.mealType]       'dejeuner' | 'diner' | 'souper' | 'collation' | 'repas'
 * @param {number} [args.portions]
 * @param {string} [args.targetDate]     'YYYY-MM-DD' for history mode
 * @param {boolean} [args.ramadanOverride]
 * @param {boolean} [args.culturalOverride]
 * @param {string} [args.idempotencyKey]
 * @param {(phase:string, data:object)=>void} [args.onPhase]
 * @returns {Promise<object>} Final validated analysis payload.
 */
export async function analyzeMealPhotoStream({
  base64, mealType, portions, targetDate, ramadanOverride, culturalOverride,
  idempotencyKey, onPhase, dryRun = false,
}) {
  ScanTelemetry.startScanSession(idempotencyKey, { endpoint: 'analyze-stream' });
  ScanTelemetry.trackEvent('scan_started', { endpoint: 'analyze-stream', idempotency_key: idempotencyKey });
  const sessionStart = Date.now();
  let firstEventAt = null;
  ErrorLogger.breadcrumb('meal:stream:open');

  // Resolve the absolute URL via axios' baseURL so the fetch matches the
  // same proxying rules as our REST calls.
  const baseURL = api.defaults.baseURL || '';
  const url = `${baseURL.replace(/\/$/, '')}/meals/analyze-stream`;
  // Pull the session token from localStorage (same key the axios interceptor uses).
  const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('session_token') : null;
  const headers = {
    'Content-Type': 'application/json',
    // Iter. 404 v2 — SSE wire format so Cloudflare auto-bypasses buffering.
    Accept: 'text/event-stream',
  };
  if (token) headers['X-Session-Token'] = token;

  const ctrl = new AbortController();
  // Outer cap — generous because the server caps each LLM attempt at 13s.
  const timeoutId = setTimeout(() => ctrl.abort('STREAM_TIMEOUT'), API_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        image_base64: base64,
        meal_type: mealType || 'repas',
        portions: portions || 1,
        ramadan_override: !!ramadanOverride,
        cultural_override: !!culturalOverride,
        target_date: targetDate || undefined,
        idempotency_key: idempotencyKey || undefined,
        dry_run: !!dryRun,
      }),
      signal: ctrl.signal,
    });
  } catch (fetchErr) {
    clearTimeout(timeoutId);
    ScanTelemetry.endScanSession();
    throw fetchErr;
  }

  if (!response.ok) {
    clearTimeout(timeoutId);
    // Build an axios-shaped error so the rest of the app handles it the same.
    let detail = response.statusText;
    try {
      const payload = await response.json();
      detail = payload?.detail || detail;
    } catch { /* noop */ }
    const err = new Error(detail);
    err.response = { status: response.status, data: { detail } };
    ScanTelemetry.endScanSession();
    throw err;
  }

  if (!response.body || typeof response.body.getReader !== 'function') {
    clearTimeout(timeoutId);
    ScanTelemetry.endScanSession();
    throw new Error('STREAM_NOT_SUPPORTED');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finalPayload = null;
  let blockEvent = null;
  let errorEvent = null;

  // Iter. 404 v2 — SSE wire format: events are separated by `\n\n` and each
  // event payload is prefixed with `data: `. This format bypasses
  // Cloudflare's response buffering at the edge (which was eating our
  // initial NDJSON stream).
  const parseSseEvent = (rawBlock) => {
    const lines = rawBlock.split('\n');
    let dataLine = '';
    for (const ln of lines) {
      if (ln.startsWith('data:')) {
        dataLine += ln.slice(5).trimStart();
      }
    }
    if (!dataLine) return null;
    try { return JSON.parse(dataLine); } catch { return null; }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE separator is `\n\n` (double newline = end of event).
      let sepIdx;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawBlock = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const evt = parseSseEvent(rawBlock);
        if (!evt) continue;
        if (firstEventAt === null) {
          firstEventAt = Date.now();
          ErrorLogger.breadcrumb('meal:stream:first-event', {
            phase: evt.phase, time_to_first_event_ms: firstEventAt - sessionStart,
          });
        }
        if (typeof onPhase === 'function') {
          try { onPhase(evt.phase, evt); } catch { /* swallow */ }
        }
        if (evt.phase === 'complete') {
          finalPayload = evt;
        } else if (evt.phase === 'block') {
          blockEvent = evt;
        } else if (evt.phase === 'error') {
          errorEvent = evt;
        }
      }
    }
    // Flush remaining buffer (rare, but possible if server didn't end with \n\n)
    if (buffer.trim()) {
      const evt = parseSseEvent(buffer);
      if (evt) {
        if (typeof onPhase === 'function') onPhase(evt.phase, evt);
        if (evt.phase === 'complete') finalPayload = evt;
        else if (evt.phase === 'block') blockEvent = evt;
        else if (evt.phase === 'error') errorEvent = evt;
      }
    }
  } finally {
    clearTimeout(timeoutId);
    try { reader.releaseLock(); } catch { /* noop */ }
  }

  if (errorEvent) {
    const err = new Error(errorEvent.detail || 'Stream error');
    err.response = { status: errorEvent.status || 502, data: { detail: errorEvent.detail } };
    ScanTelemetry.endScanSession();
    throw err;
  }
  if (blockEvent) {
    // Mirror the legacy /meals/analyze response shape so the caller's
    // existing `safeData.ramadan_block` / `safeData.cultural_block` branches
    // handle it without code changes.
    ScanTelemetry.trackEvent('scan_completed', {
      endpoint: 'analyze-stream', idempotency_key: idempotencyKey,
      latency_ms: Date.now() - sessionStart, blocked: blockEvent.block_type,
    });
    ScanTelemetry.endScanSession();
    if (blockEvent.block_type === 'ramadan') {
      return {
        ramadan_block: true,
        fasting_info: blockEvent.fasting_info,
        message: blockEvent.message,
        can_override: blockEvent.can_override,
      };
    }
    return {
      cultural_block: true,
      compliance: blockEvent.compliance,
      yuri_alert: blockEvent.yuri_alert,
      analysis: blockEvent.analysis,
      can_override: blockEvent.can_override,
    };
  }
  if (!finalPayload) {
    ScanTelemetry.endScanSession();
    throw new Error('STREAM_ENDED_WITHOUT_COMPLETE');
  }

  ScanTelemetry.trackEvent('scan_completed', {
    endpoint: 'analyze-stream',
    idempotency_key: idempotencyKey,
    latency_ms: Date.now() - sessionStart,
    time_to_first_event_ms: firstEventAt ? (firstEventAt - sessionStart) : null,
  });
  ScanTelemetry.endScanSession();
  // Strip the `phase` field — the caller wants the same shape as legacy analyze.
  const { phase: _phase, ...payload } = finalPayload;
  return validateAnalysisShape(payload);
}

/**
 * Send a text meal description for streaming analysis.
 * Same shape & semantics as `analyzeMealPhotoStream` but for the text path.
 */
export async function analyzeMealTextStream({
  description, mealType, portions, targetDate, ramadanOverride, culturalOverride,
  idempotencyKey, onPhase, dryRun = false,
}) {
  ScanTelemetry.startScanSession(idempotencyKey, { endpoint: 'analyze-text-stream' });
  ScanTelemetry.trackEvent('scan_started', { endpoint: 'analyze-text-stream', idempotency_key: idempotencyKey });
  const sessionStart = Date.now();
  let firstEventAt = null;
  ErrorLogger.breadcrumb('meal:text-stream:open');

  const baseURL = api.defaults.baseURL || '';
  const url = `${baseURL.replace(/\/$/, '')}/meals/analyze-text-stream`;
  const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('session_token') : null;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) headers['X-Session-Token'] = token;

  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort('STREAM_TIMEOUT'), API_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        description,
        meal_type: mealType || 'repas',
        portions: portions || 1,
        ramadan_override: !!ramadanOverride,
        cultural_override: !!culturalOverride,
        target_date: targetDate || undefined,
        idempotency_key: idempotencyKey || undefined,
        dry_run: !!dryRun,
      }),
      signal: ctrl.signal,
    });
  } catch (fetchErr) {
    clearTimeout(timeoutId);
    ScanTelemetry.endScanSession();
    throw fetchErr;
  }

  if (!response.ok) {
    clearTimeout(timeoutId);
    let detail = response.statusText;
    try { const payload = await response.json(); detail = payload?.detail || detail; } catch { /* noop */ }
    const err = new Error(detail);
    err.response = { status: response.status, data: { detail } };
    ScanTelemetry.endScanSession();
    throw err;
  }

  if (!response.body || typeof response.body.getReader !== 'function') {
    clearTimeout(timeoutId);
    ScanTelemetry.endScanSession();
    throw new Error('STREAM_NOT_SUPPORTED');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finalPayload = null;
  let blockEvent = null;
  let errorEvent = null;

  const parseSseEvent = (rawBlock) => {
    const lines = rawBlock.split('\n');
    let dataLine = '';
    for (const ln of lines) {
      if (ln.startsWith('data:')) dataLine += ln.slice(5).trimStart();
    }
    if (!dataLine) return null;
    try { return JSON.parse(dataLine); } catch { return null; }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sepIdx;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawBlock = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const evt = parseSseEvent(rawBlock);
        if (!evt) continue;
        if (firstEventAt === null) {
          firstEventAt = Date.now();
          ErrorLogger.breadcrumb('meal:text-stream:first-event', {
            phase: evt.phase, time_to_first_event_ms: firstEventAt - sessionStart,
          });
        }
        if (typeof onPhase === 'function') {
          try { onPhase(evt.phase, evt); } catch { /* swallow */ }
        }
        if (evt.phase === 'complete') finalPayload = evt;
        else if (evt.phase === 'block') blockEvent = evt;
        else if (evt.phase === 'error') errorEvent = evt;
      }
    }
    if (buffer.trim()) {
      const evt = parseSseEvent(buffer);
      if (evt) {
        if (typeof onPhase === 'function') onPhase(evt.phase, evt);
        if (evt.phase === 'complete') finalPayload = evt;
        else if (evt.phase === 'block') blockEvent = evt;
        else if (evt.phase === 'error') errorEvent = evt;
      }
    }
  } finally {
    clearTimeout(timeoutId);
    try { reader.releaseLock(); } catch { /* noop */ }
  }

  if (errorEvent) {
    const err = new Error(errorEvent.detail || 'Stream error');
    err.response = { status: errorEvent.status || 502, data: { detail: errorEvent.detail } };
    ScanTelemetry.endScanSession();
    throw err;
  }
  if (blockEvent) {
    ScanTelemetry.endScanSession();
    if (blockEvent.block_type === 'ramadan') {
      return {
        ramadan_block: true,
        fasting_info: blockEvent.fasting_info,
        message: blockEvent.message,
        can_override: blockEvent.can_override,
      };
    }
    return {
      cultural_block: true,
      compliance: blockEvent.compliance,
      yuri_alert: blockEvent.yuri_alert,
      analysis: blockEvent.analysis,
      can_override: blockEvent.can_override,
    };
  }
  if (!finalPayload) {
    ScanTelemetry.endScanSession();
    throw new Error('STREAM_ENDED_WITHOUT_COMPLETE');
  }

  ScanTelemetry.trackEvent('scan_completed', {
    endpoint: 'analyze-text-stream',
    idempotency_key: idempotencyKey,
    latency_ms: Date.now() - sessionStart,
    time_to_first_event_ms: firstEventAt ? (firstEventAt - sessionStart) : null,
  });
  ScanTelemetry.endScanSession();
  const { phase: _phase, ...payload } = finalPayload;
  return validateAnalysisShape(payload);
}


// ============================================================================
// CONFIG
// ============================================================================
const MAX_INPUT_BYTES = 25 * 1024 * 1024; // hard reject before decode (25 MB)
const TARGET_OUTPUT_BYTES = 500 * 1024;    // < 500 KB after compression
const COMPRESS_MAX_WIDTH = 800;
const COMPRESS_QUALITY = 0.6;
// 120 s — covers OpenAI Vision cold-start (45 s) + backend post-processing
// (compliance check, badges, idempotency replay) on slow mobile networks.
// The Kubernetes ingress in front of YUMIX caps requests around 60 s, so
// when the primary call dies we fall through to the by-idempotency poll
// (recoverMealByIdempotency) which adds another ~18 s of patient retry.
// Iter. 329 — bumped 90s → 120s for industrial resilience (target: 500-tester rollout).
// Per-attempt timeout — kept generous enough for vision LLMs but tight enough
// to make a stuck request fail fast and trigger the retry fallback path,
// instead of leaving the user staring at a frozen loader (Sentinelle telem:
// scan_loader_stuck_over_60s). Worst case now: 60s × (1 + 1 retry) = 120s.
const API_TIMEOUT_MS = 60000;

// Iter. 336 — Yuri spec for Samsung S20/S21 FE stability:
// 2 silent auto-retries on transient errors (timeout / 502 / 503 / 504 /
// network drop) before bubbling the failure to the user. Same idempotency
// key on every retry → backend dedupes, OpenAI is NOT charged twice.
const RETRY_MAX_ATTEMPTS = 1;
const RETRY_BACKOFF_MS = 800; // 1st retry after 800ms, 2nd after 1600ms

// 5 s — after this many seconds spent on a single API call we fire the
// `onSlowFeedback` callback so the UI can reassure the user the app is alive.
const SLOW_FEEDBACK_AFTER_MS = 5000;

// ============================================================================
// IDEMPOTENCY
// ============================================================================
/**
 * Generates a UUID v4 idempotency key. The SAME key is sent on every retry
 * of the same logical analysis attempt. Backend dedups by (user_id, key).
 *
 * Foundation invariant: a single user gesture (one "scan" or one "analyze")
 * MUST never produce two meals in DB nor charge OpenAI twice.
 */
export function newIdempotencyKey() {
  // Prefer crypto.randomUUID where available (all modern browsers + RN)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older webviews
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ============================================================================
// SHARED HELPERS
// ============================================================================
function isHEIC(file) {
  const name = (file?.name || '').toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif')
    || file?.type === 'image/heic' || file?.type === 'image/heif';
}

function fr(language) { return (language || 'fr').startsWith('fr'); }

/** Standardized "Analyse difficile" toast text.
 *  Iter. 329 — softened message so client knows we still have the result
 *  in flight (recovery polling runs for ~18 s after the timeout). */
export function difficultAnalysisMessage(language) {
  return fr(language)
    ? 'Analyse plus longue que prévue, on garde le résultat pour toi dès qu\'il est prêt'
    : 'Analysis taking longer than expected — we\'ll save your result as soon as it lands';
}

/** Map an HTTP/timeout error to a user-actionable FR/EN message. */
export function mapAnalysisError(error, language, kind = 'photo') {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const isTimeout = error?.name === 'CanceledError'
    || error?.code === 'ECONNABORTED'
    || String(error?.message || '').toLowerCase().includes('timeout');
  const isFr = fr(language);
  // 🚦 PAYWALL (402) — the global axios interceptor already opened PaywallModal.
  // We must NOT show an extra toast, NOT log CRITICAL, NOT close the analysis
  // modal with an error state. The caller checks `isPaywall` to short-circuit.
  if (status === 402) {
    return { msg: '', status, isTimeout: false, isPaywall: true };
  }
  if (isTimeout) return { msg: difficultAnalysisMessage(language), status, isTimeout: true, isPaywall: false };
  if (detail) return { msg: typeof detail === 'string' ? detail : (detail.message || ''), status, isTimeout: false, isPaywall: false };
  let msg;
  if (kind === 'photo') {
    if (status === 413) msg = isFr ? 'Photo trop lourde. Reprends-la plus compacte.' : 'Photo too large. Retake it smaller.';
    else if (status === 415) msg = isFr ? "Format photo non supporté (HEIC/AVIF ?). Convertis en JPEG." : 'Unsupported photo format (HEIC/AVIF?). Convert to JPEG.';
    else if (status === 422) msg = isFr ? "Aucun aliment détecté. Rapproche-toi ou améliore l'éclairage." : 'No food detected. Move closer or improve lighting.';
    else if (status === 429) msg = isFr ? "L'IA est saturée. Réessaie dans 10-20 s." : 'AI rate-limited. Retry in 10-20s.';
    else if (status === 502) msg = isFr ? "L'IA a renvoyé une erreur. Réessaie." : 'AI returned an error. Retry.';
    else if (status === 504) msg = isFr ? "Le serveur IA n'a pas répondu. Réessaie." : 'AI server timed out. Retry.';
    else if (status >= 520 && status <= 526) msg = isFr ? "Le serveur YUMIX redémarre. Réessaie dans 10-20 s." : 'YUMIX server restarting. Retry in 10-20s.';
    else if (!status) msg = isFr ? 'Connexion interrompue. Vérifie ton réseau.' : 'Connection interrupted. Check your network.';
    else msg = isFr ? "Erreur d'analyse — réessaie." : 'Analysis error — retry.';
  } else {
    if (status === 422) msg = isFr ? "Description trop vague — ajoute des aliments précis (ex: 200g poulet, 150g riz)." : 'Description too vague — add specific foods.';
    else if (status === 429) msg = isFr ? "L'IA est saturée. Réessaie dans 10-20 s." : 'AI rate-limited. Retry in 10-20s.';
    else if (status === 502) msg = isFr ? "L'IA a renvoyé une erreur. Réessaie." : 'AI returned an error. Retry.';
    else if (status === 504) msg = isFr ? "Le serveur IA n'a pas répondu. Réessaie." : 'AI server timed out. Retry.';
    else if (status >= 520 && status <= 526) msg = isFr ? "Le serveur YUMIX redémarre. Réessaie dans 10-20 s." : 'YUMIX server restarting. Retry in 10-20s.';
    else if (!status) msg = isFr ? 'Connexion interrompue. Vérifie ton réseau.' : 'Connection interrupted. Check your network.';
    else msg = isFr ? "Erreur d'analyse — réessaie." : 'Analysis error — retry.';
  }
  return { msg, status, isTimeout: false, isPaywall: false };
}

// ============================================================================
// PHOTO PATH
// ============================================================================
/**
 * Throws a typed Error if the file is unfit for processing.
 * Does NOT decode the bytes — pure metadata checks (size, MIME, ext).
 */
export function validatePhotoFile(file, language) {
  if (!file) throw new Error('NO_FILE');
  if (file.type && !file.type.startsWith('image/')) throw new Error('NOT_AN_IMAGE');
  if (isHEIC(file)) {
    const err = new Error('HEIC_UNSUPPORTED');
    err.userMessage = fr(language)
      ? 'Format HEIC non supporté. Dans Réglages iOS > Appareil photo > Formats, choisis "Le plus compatible" (JPEG).'
      : 'HEIC format not supported. In iOS Settings > Camera > Formats, select "Most Compatible" (JPEG).';
    throw err;
  }
  if (file.size > MAX_INPUT_BYTES) {
    const err = new Error('FILE_TOO_LARGE');
    err.userMessage = fr(language)
      ? `Photo trop lourde (${Math.round(file.size / 1024 / 1024)} MB). Max 25 MB — réduis la qualité dans ta galerie puis réessaie.`
      : `Photo too large (${Math.round(file.size / 1024 / 1024)} MB). Max 25 MB — reduce quality in your gallery then retry.`;
    throw err;
  }
}

/**
 * Compress a File to base64 < 500 KB (JPEG, max 800 px).
 * Drops the File reference after compression and yields one frame to the
 * compositor so the renderer can release the decoded bitmap before we
 * launch the network call (anti-OOM iOS Safari).
 *
 * Returns { base64, sizeKb, reductionPct, durationMs }.
 */
export async function compressForUpload(file) {
  ErrorLogger.breadcrumb('meal:compress:start', { size_kb: Math.round(file.size / 1024) });
  const t0 = performance.now();
  const base64 = await compressImage(file, { maxWidth: COMPRESS_MAX_WIDTH, quality: COMPRESS_QUALITY });
  // Validate the encoded payload is sane base64 (no data: prefix, charset OK).
  if (!base64 || typeof base64 !== 'string' || base64.length < 100
      || /[^A-Za-z0-9+/=]/.test(base64.slice(0, 100))) {
    throw new Error('COMPRESSION_PRODUCED_INVALID_BASE64');
  }
  const sizeBytes = Math.ceil((base64.length * 3) / 4);
  const sizeKb = Math.round(sizeBytes / 1024);
  const reductionPct = file.size > 0 ? Math.round((1 - sizeBytes / file.size) * 100) : 0;
  const durationMs = Math.round(performance.now() - t0);
  ErrorLogger.breadcrumb('meal:compress:done', { size_kb: sizeKb, reduction_pct: reductionPct });

  // Hard ceiling: if somehow > TARGET_OUTPUT_BYTES, log it (we don't fail
  // because the ladder in compressImage already does its best).
  if (sizeBytes > TARGET_OUTPUT_BYTES * 2) {
    ErrorLogger.log(
      { message: `Compression exceeded 2× target (${sizeKb} KB)` },
      'Meal_Compression_Oversized',
      { severity: 'WARN', size_kb: sizeKb, reduction_pct: reductionPct }
    );
  }

  // Yield to compositor + opportunistic GC so the renderer can reclaim
  // the decoded ImageBitmap before we add network pressure.
  ErrorLogger.breadcrumb('meal:gc:yield');
  await new Promise((r) => setTimeout(r, 50));
  // eslint-disable-next-line no-undef
  try { if (typeof window !== 'undefined' && typeof window.gc === 'function') window.gc(); } catch { /* noop */ }

  return { base64, sizeKb, reductionPct, durationMs };
}

// ============================================================================
// CLIENT-SIDE FAILURE REPORTING (fire-and-forget)
// ============================================================================
// When a scan request fails on the client (timeout / abort / network drop), the
// server middleware never sees it → `scan_telemetry` stays green while users
// rage-retry. This tiny beacon closes that observability gap (2026-05-01 fix).
async function reportClientScanFailure({ endpoint, error, startedAt, payloadBytes }) {
  try {
    const latency_ms = startedAt ? Math.max(0, Date.now() - startedAt) : undefined;
    const isAbort = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.message === 'canceled';
    const isTimeout = error?.code === 'ECONNABORTED' || String(error?.message || '').toLowerCase().includes('timeout') || isAbort;
    const isNetwork = !error?.response && !isTimeout;
    const error_code = isTimeout ? 'CLIENT_TIMEOUT' : isNetwork ? 'CLIENT_NETWORK' : 'CLIENT_UNKNOWN';
    const detail = String(error?.message || error?.code || '').slice(0, 200);
    // Best-effort — never throws, never blocks the user UI
    api.post('/meals/client-failure', {
      endpoint, error_code, detail, latency_ms, payload_bytes: payloadBytes,
    }, { timeout: 5000 }).catch(() => {});
  } catch {
    // absorb
  }
}

/**
 * Poll the backend for a meal saved under `idempotencyKey`. Used as a recovery
 * step when `/meals/analyze` is cut off (504 ingress timeout, network drop)
 * AFTER the backend had already saved the meal. The Kubernetes ingress in
 * front of YUMIX caps requests at ~60 s so a 35 s OpenAI Vision call combined
 * with backend post-processing can land just over that boundary; the meal
 * reaches Mongo but the response never reaches the browser. Without this
 * poll the user would see "back to dashboard" with an orphaned meal.
 *
 * Strategy: 6 attempts × 3 s spacing = up to ~18 s of patient retry.
 * Returns the validated analysis or `null` if the meal never showed up.
 */
export async function recoverMealByIdempotency(idempotencyKey, { attempts = 6, delayMs = 3000 } = {}) {
  if (!idempotencyKey) return null;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await api.get(`/meals/by-idempotency/${encodeURIComponent(idempotencyKey)}`, { timeout: 8000 });
      if (res?.data?.meal_id) {
        ErrorLogger.breadcrumb('meal:recover:hit', { attempt: i + 1, meal_id: res.data.meal_id });
        return validateAnalysisShape(res.data);
      }
    } catch (err) {
      // 404 = not yet. Anything else = stop polling, surface original error.
      if (err?.response?.status && err.response.status !== 404) {
        ErrorLogger.breadcrumb('meal:recover:fatal', { status: err.response.status });
        return null;
      }
      ErrorLogger.breadcrumb('meal:recover:miss', { attempt: i + 1 });
    }
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return null;
}

/**
 * Detect if an error is "transient" (worth a silent retry).
 */
function isTransientError(err) {
  const status = err?.response?.status;
  const isAbort = err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
  const isTimeout = err?.code === 'ECONNABORTED' || isAbort
    || String(err?.message || '').toLowerCase().includes('timeout');
  // Treat the Cloudflare edge codes (520-526) as transient too — they bubble
  // up when the origin is restarting / momentarily unreachable, which is
  // exactly the kind of hiccup a silent retry should swallow.
  return !status || isTimeout
    || status === 502 || status === 503 || status === 504
    || (status >= 520 && status <= 526);
}

/**
 * Call the photo analysis API. Single timeout, single error path,
 * standardized "Analyse difficile" mapping.
 *
 * Iter. 336: 2 silent auto-retries on transient errors before bubbling up.
 * Same idempotency_key on every attempt → backend dedupes, OpenAI not billed twice.
 *
 * @param {Function} [onSlowFeedback]  Called once per attempt after 5s of waiting.
 *
 * Returns the validated analysis payload (shape-safe defaults applied).
 * Throws on persistent network/HTTP errors — caller handles via mapAnalysisError().
 */
export async function analyzeMealPhoto({
  base64, mealType, portions, targetDate, ramadanOverride, idempotencyKey,
  onSlowFeedback,
}) {
  // 🛡️ Indestructible telemetry — start heartbeat session (30 s pings)
  ScanTelemetry.startScanSession(idempotencyKey, { endpoint: 'analyze' });
  ScanTelemetry.trackEvent('scan_started', { endpoint: 'analyze', idempotency_key: idempotencyKey });
  const sessionStart = Date.now();
  let lastErr;
  for (let attempt = 0; attempt <= RETRY_MAX_ATTEMPTS; attempt++) {
    ErrorLogger.breadcrumb('meal:photo:api:about-to-call', { attempt: attempt + 1 });
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort('TIMEOUT'), API_TIMEOUT_MS);
    const slowId = (typeof onSlowFeedback === 'function')
      ? setTimeout(() => { try { onSlowFeedback(attempt); } catch { /* noop */ } }, SLOW_FEEDBACK_AFTER_MS)
      : null;
    const startedAt = Date.now();
    const payloadBytes = base64 ? Math.floor(base64.length * 0.75) : undefined;
    try {
      const response = await api.post('/meals/analyze', {
        image_base64: base64,
        meal_type: mealType || 'repas',
        portions: portions || 1,
        ramadan_override: !!ramadanOverride,
        target_date: targetDate || undefined,
        idempotency_key: idempotencyKey || undefined,
      }, { signal: ctrl.signal, timeout: API_TIMEOUT_MS });
      clearTimeout(timeoutId);
      if (slowId) clearTimeout(slowId);
      ErrorLogger.breadcrumb('meal:photo:api:done', {
        status: response.status, dedup: !!response.data?.deduplicated, attempt: attempt + 1,
      });
      ScanTelemetry.trackEvent('scan_completed', {
        endpoint: 'analyze', idempotency_key: idempotencyKey,
        latency_ms: Date.now() - sessionStart, attempts: attempt + 1,
      });
      ScanTelemetry.endScanSession();
      return validateAnalysisShape(response.data);
    } catch (err) {
      clearTimeout(timeoutId);
      if (slowId) clearTimeout(slowId);
      lastErr = err;

      // 🛡️ Recovery — when request times out AFTER backend saved the meal.
      if (isTransientError(err) && idempotencyKey) {
        ErrorLogger.breadcrumb('meal:photo:recover:start', {
          attempt: attempt + 1,
          reason: err?.response?.status || 'timeout-or-network',
        });
        const recovered = await recoverMealByIdempotency(idempotencyKey);
        if (recovered) {
          ErrorLogger.breadcrumb('meal:photo:recover:ok', { attempt: attempt + 1 });
          ScanTelemetry.trackEvent('scan_completed', {
            endpoint: 'analyze', idempotency_key: idempotencyKey,
            latency_ms: Date.now() - sessionStart, attempts: attempt + 1,
            recovered: true,
          });
          ScanTelemetry.endScanSession();
          return recovered;
        }
      }

      // Track offline drops as their own bucket — easy to filter in admin.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        ScanTelemetry.trackEvent('scan_offline_drop', {
          endpoint: 'analyze', idempotency_key: idempotencyKey,
          detail: String(err?.message || '').slice(0, 200),
        });
      }

      // Track 422 "no food detected" — that's a UX issue we want to count.
      if (err?.response?.status === 422) {
        ScanTelemetry.trackEvent('scan_no_food_detected', {
          endpoint: 'analyze', idempotency_key: idempotencyKey,
          detail: String(err?.response?.data?.detail || '').slice(0, 200),
        });
      }

      // Auto-retry the FULL call if the error was transient and we have attempts left.
      if (isTransientError(err) && attempt < RETRY_MAX_ATTEMPTS) {
        const backoff = RETRY_BACKOFF_MS * (attempt + 1);
        ErrorLogger.breadcrumb('meal:photo:retry', { attempt: attempt + 1, backoff_ms: backoff });
        await new Promise((r) => setTimeout(r, backoff));
        continue;
      }

      if (!err?.response) {
        reportClientScanFailure({ endpoint: 'analyze', error: err, startedAt, payloadBytes });
      }
      ScanTelemetry.endScanSession();
      throw err;
    }
  }
  ScanTelemetry.endScanSession();
  throw lastErr || new Error('UNKNOWN_ANALYZE_FAILURE');
}

// ============================================================================
// TEXT PATH
// ============================================================================
/**
 * Validate a text description before sending. Throws on bad input.
 */
export function validateTextDescription(description) {
  if (!description || description.trim().length < 5) {
    throw new Error('DESCRIPTION_TOO_SHORT');
  }
}

/**
 * Call the text analysis API. Same robustness as the photo path —
 * including by-idempotency recovery + 2 silent auto-retries (Iter. 336).
 */
export async function analyzeMealText({
  description, mealType, portions, targetDate, ramadanOverride, idempotencyKey,
  onSlowFeedback,
}) {
  ScanTelemetry.startScanSession(idempotencyKey, { endpoint: 'analyze-text' });
  ScanTelemetry.trackEvent('scan_started', { endpoint: 'analyze-text', idempotency_key: idempotencyKey });
  const sessionStart = Date.now();
  let lastErr;
  for (let attempt = 0; attempt <= RETRY_MAX_ATTEMPTS; attempt++) {
    ErrorLogger.breadcrumb('meal:text:api:about-to-call', { attempt: attempt + 1 });
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort('TIMEOUT'), API_TIMEOUT_MS);
    const slowId = (typeof onSlowFeedback === 'function')
      ? setTimeout(() => { try { onSlowFeedback(attempt); } catch { /* noop */ } }, SLOW_FEEDBACK_AFTER_MS)
      : null;
    const startedAt = Date.now();
    try {
      const response = await api.post('/meals/analyze-text', {
        description,
        meal_type: mealType || 'repas',
        portions: portions || 1,
        ramadan_override: !!ramadanOverride,
        target_date: targetDate || undefined,
        idempotency_key: idempotencyKey || undefined,
      }, { signal: ctrl.signal, timeout: API_TIMEOUT_MS });
      clearTimeout(timeoutId);
      if (slowId) clearTimeout(slowId);
      ErrorLogger.breadcrumb('meal:text:api:done', {
        status: response.status, dedup: !!response.data?.deduplicated, attempt: attempt + 1,
      });
      ScanTelemetry.trackEvent('scan_completed', {
        endpoint: 'analyze-text', idempotency_key: idempotencyKey,
        latency_ms: Date.now() - sessionStart, attempts: attempt + 1,
      });
      ScanTelemetry.endScanSession();
      return validateAnalysisShape(response.data);
    } catch (err) {
      clearTimeout(timeoutId);
      if (slowId) clearTimeout(slowId);
      lastErr = err;

      if (isTransientError(err) && idempotencyKey) {
        ErrorLogger.breadcrumb('meal:text:recover:start', {
          attempt: attempt + 1,
          reason: err?.response?.status || 'timeout-or-network',
        });
        const recovered = await recoverMealByIdempotency(idempotencyKey);
        if (recovered) {
          ErrorLogger.breadcrumb('meal:text:recover:ok', { attempt: attempt + 1 });
          ScanTelemetry.trackEvent('scan_completed', {
            endpoint: 'analyze-text', idempotency_key: idempotencyKey,
            latency_ms: Date.now() - sessionStart, attempts: attempt + 1, recovered: true,
          });
          ScanTelemetry.endScanSession();
          return recovered;
        }
      }

      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        ScanTelemetry.trackEvent('scan_offline_drop', {
          endpoint: 'analyze-text', idempotency_key: idempotencyKey,
          detail: String(err?.message || '').slice(0, 200),
        });
      }
      if (err?.response?.status === 422) {
        ScanTelemetry.trackEvent('scan_no_food_detected', {
          endpoint: 'analyze-text', idempotency_key: idempotencyKey,
          detail: String(err?.response?.data?.detail || '').slice(0, 200),
        });
      }

      if (isTransientError(err) && attempt < RETRY_MAX_ATTEMPTS) {
        const backoff = RETRY_BACKOFF_MS * (attempt + 1);
        ErrorLogger.breadcrumb('meal:text:retry', { attempt: attempt + 1, backoff_ms: backoff });
        await new Promise((r) => setTimeout(r, backoff));
        continue;
      }

      if (!err?.response) {
        reportClientScanFailure({ endpoint: 'analyze-text', error: err, startedAt });
      }
      ScanTelemetry.endScanSession();
      throw err;
    }
  }
  ScanTelemetry.endScanSession();
  throw lastErr || new Error('UNKNOWN_ANALYZE_TEXT_FAILURE');
}
